import { LABEEB_CONFIG, type LabeebConfigInfo } from "../../api/labeeb";
import type { AppConfig } from "../../config";
import { useResource } from "../../hooks/useResource";
import { ResourceView } from "../states";
import { Notice } from "../ui";

/**
 * What this deployment of Labeeb is: versions, the tier a new business gets,
 * and how soon each kind of change takes effect. Nothing here is writable.
 */
export function DeploymentFacts({ config }: { config: AppConfig }) {
  const facts = useResource<LabeebConfigInfo>(config, LABEEB_CONFIG);
  return (
    <ResourceView state={facts.state} what="deployment facts" onRetry={facts.reload}>
      {(data) => (
        <div className="text-sm">
          <p>
            API <span className="ref">{data.admin_api_version ?? "—"}</span>
          </p>
          <p>
            Model <span className="ref">{data.model_version ?? "—"}</span>
          </p>
          <p>
            Default tier <span className="ref">{data.default_tier ?? "—"}</span>
          </p>
          {(data.effects ?? []).length > 0 && (
            <table className="mt-2 text-xs">
              <tbody>
                {data.effects!.map((row) => (
                  <tr key={row.resource ?? row.effect}>
                    <td className="pr-3 text-slate-500 dark:text-slate-400">{row.resource}</td>
                    <td>{row.effect}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </ResourceView>
  );
}

/**
 * What the Labeeb audit log covers, in Labeeb's own words (`audit_history.covers`).
 *
 * The log starts at the admin API's first deploy and holds only changes made
 * through it. Saying nothing would let an operator treat it as the business's
 * whole history.
 */
export function AuditCoverageNote({ config }: { config: AppConfig }) {
  const facts = useResource<LabeebConfigInfo>(config, LABEEB_CONFIG);
  if (facts.state.kind === "loading") return null;
  if (facts.state.kind !== "ready") {
    return (
      <div className="mb-3">
        <Notice tone="amber" title="Could not read what this log covers">
          The log below holds only changes made through Labeeb's admin API, not the
          business's whole history.
        </Notice>
      </div>
    );
  }
  const covers = facts.state.data.audit_history?.covers;
  return (
    <div className="mb-3">
      <Notice title="What this log covers">
        {typeof covers === "string" && covers
          ? covers
          : "Only changes made through Labeeb's admin API, since its first deploy."}
      </Notice>
    </div>
  );
}
